import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FailIconDisplay from '../ui/FailIconDisplay';
import { applicationStore } from 'src/store/ApplicationStore';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { autorun, toJS } from 'mobx';
import { socketInprogressServiceStatus } from 'src/enums/socketInprogressServiceStatus';
import { getButtonsForNode } from 'src/pages/processing/config/errorButtonConfig';
import {
    SERVICE_REASON_MASK_RULES,
    VERIFICATION_REASON_MASK_RULES,
    getMatchingMaskRule,
} from 'src/pages/processing/config/serviceReasonConfig';

const ErrorPageComponent = observer(() => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const defaultErrorObj = {
        header: t('errors.errorOccurredTitle'),
        description: t('errors.tryAgainLater'),
        footerDescription: "",
        footerLink: "",
        footerLinkText: "",
        listItems: [],
        buttonList: [],
    };

    const formatReason = useCallback((service, defaultReason) => {
        const reason = service?.reason;
        const status = service?.status;

        const serviceRule = getMatchingMaskRule(reason, status, SERVICE_REASON_MASK_RULES);
        if (serviceRule) return t(serviceRule.translationKey);

        const verificationRule = getMatchingMaskRule(reason, status, VERIFICATION_REASON_MASK_RULES);
        if (verificationRule) return t(verificationRule.translationKey);

        return reason || defaultReason;
    }, [t]);

    useEffect(() => {
        const disposer = autorun(() => {
            let inprogressServices = toJS(applicationStore.workflowNextServiceResponse?.inprogress_services) || [];

            if (!Array.isArray(inprogressServices) || inprogressServices.length === 0) return;

            inprogressServices = inprogressServices.filter(service => service.microservice_id !== 'pep');

            const hasFailed = inprogressServices.some(
                (service) => service?.status === socketInprogressServiceStatus.FAILED
            );

            const hasRetry = inprogressServices.some(
                (service) => service?.status === socketInprogressServiceStatus.RETRY
            );

            const filteredErrorServices = inprogressServices.filter((service) =>
                (service?.status === socketInprogressServiceStatus.RETRY ||
                    service?.status === socketInprogressServiceStatus.FAILED) &&
                service?.status !== socketInprogressServiceStatus.COMPLETED
            );

            if ((hasFailed || hasRetry) && filteredErrorServices.length > 0) {
                applicationStore.setApplicationErrorObj({
                    header: t('processing.retryHeader'),
                    description: t('processing.retryDescription'),
                    listItems: filteredErrorServices.map((service) => {
                        const baseDescription = service?.node_settings_for_player?.label ||
                            t(`nodeTypes.${service.microservice_id}`, {
                                defaultValue: service.microservice_id
                            });
                        const defaultReason = service?.status === socketInprogressServiceStatus.FAILED
                            ? t('processing.defaultFailedReason')
                            : t('processing.defaultRetryReason');

                        const buttons = getButtonsForNode(
                            service.microservice_id,
                            navigate,
                            t,
                            service?.node_id?.toString(),
                            service?.node_settings_for_player
                        );

                        return {
                            baseDescription,
                            reason: formatReason(service, defaultReason),
                            buttons: buttons.length > 0 ? buttons : undefined
                        };
                    })
                });
            } else if (filteredErrorServices.length === 0) {
                applicationStore.resetApplicationErrorObj();

                const allCompleted = inprogressServices.every(
                    (service) => service?.status === socketInprogressServiceStatus.COMPLETED
                );

                if (allCompleted && applicationStore?.inquiry_status === 'approved' && applicationStore?.pepStatus === 'success') {
                    setTimeout(() => {
                        navigate("/success", { replace: true });
                    }, 500);
                }
            }
        });

        return () => {
            disposer();
            applicationStore.resetApplicationErrorObj();
        };
    }, [navigate, t, formatReason]);

    const {
        header,
        description,
        footerDescription,
        footerLink,
        footerLinkText,
        listItems,
        buttonList,
    } = applicationStore.applicationErrorObj || defaultErrorObj;

    return (
        <div className="custom-container-auto">
            <div className="flex flex-col items-center pt-[10px] w-full">
                <div className="pt-2 w-full max-w-full">
                    <div>
                        <div className="text-h5 font-bold mb-2 text-flow-heading">{header || defaultErrorObj.header}</div>
                        <div className="text-body text-Gray900">{description || defaultErrorObj.description}</div>
                    </div>

                    <div className="w-[250px] h-[250px] mx-auto">
                        <div className="relative">
                            <FailIconDisplay />
                        </div>
                    </div>

                    {listItems && listItems.length > 0 && (
                        <div className="mt-4 w-full">
                            <div className="space-y-3 w-full">
                                {listItems.map((item, index) => (
                                    <div key={index} className="bg-FailRed50 rounded-2xl p-3 w-full">
                                        <div className="flex items-start w-full">
                                            <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                                <span className="text-white text-xs font-bold">×</span>
                                            </div>
                                            <div className="text-Gray900 text-sm text-left break-words overflow-wrap-anywhere flex-1 min-w-0">
                                                <span className="font-bold">{item.baseDescription}</span>
                                                {item.reason && (
                                                    <div className="mt-1">
                                                        {item.reason.includes(',') ? (
                                                            <div className="space-y-1">
                                                                {item.reason.split(',').map((reasonItem, reasonIndex) => (
                                                                    <div key={reasonIndex} className="text-sm">
                                                                        • {typeof reasonItem === 'string' ? reasonItem.trim().charAt(0).toUpperCase() + reasonItem.trim().slice(1) : reasonItem}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span>• {typeof item.reason === 'string' ? item.reason.trim().charAt(0).toUpperCase() + item.reason.trim().slice(1) : item.reason}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {item.buttons && item.buttons.length > 0 && (
                                            <div className="flex gap-4 justify-end mt-3 w-full">
                                                {item.buttons.map((button, btnIndex) => (
                                                    <Button
                                                        key={btnIndex}
                                                        type="link"
                                                        htmlType="button"
                                                        size="small"
                                                        onClick={() => {
                                                            if (button.onClick) {
                                                                button.onClick();
                                                            } else if (button.link) {
                                                                navigate(button.link, { replace: true });
                                                            }
                                                        }}
                                                        id={`item-${index}-button-${btnIndex}`}
                                                        className="text-xs font-bold text-Gray900 hover:text-SorrellBrown5000 px-0"
                                                    >
                                                        {button.label}
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {buttonList && buttonList.length > 0 && (
                        <div className="flex flex-col items-center py-1 mt-4 w-full">
                            {buttonList.map((button, index) => (
                                button.href ? (
                                    <Button
                                        key={index}
                                        href={button.href}
                                        target="_parent"
                                        id={`button-${index}`}
                                        className="mt-1 w-full max-w-xs"
                                        variant="primaryWithoutIcon"
                                    >
                                        {button.label}
                                    </Button>
                                ) : (
                                    <Button
                                        key={index}
                                        onClick={() => {
                                            if (button.onClick) {
                                                button.onClick();
                                            } else {
                                                navigate(button.link || '/', { replace: true });
                                            }
                                        }}
                                        id={`button-${index}`}
                                        className="mt-1 w-full max-w-xs"
                                        variant={button.variant || 'primaryWithoutIcon'}
                                    >
                                        {button.label}
                                    </Button>
                                )
                            ))}
                        </div>
                    )}

                    {footerDescription && (
                        <div className="text-body text-Gray900 mt-1 w-full break-words">
                            {footerDescription}
                            {footerLink && (
                                <a href={footerLink} className="text-blue-500 underline ml-2 break-words">
                                    {footerLinkText || ""}
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default ErrorPageComponent;